import { create } from 'zustand';
import { api } from '../services/api';

export type FlightStatus = 'draft' | 'active' | 'paused' | 'completed' | 'archived';

export interface FlightAIContext {
  brand_voice?: string;
  target_audience?: string;
  key_messages?: string[];
  hashtag_strategy?: string[];
  content_pillars?: string[];
}

export interface FlightGoals {
  post_count?: number;
  platforms?: string[];
  post_types?: Record<string, number>;
  posting_frequency?: string;
}

export interface FlightStats {
  posts_created?: number;
  posts_published?: number;
  images_generated?: number;
  ai_tokens_used?: number;
}

export interface FlightAttributeRef {
  id: string;
  name: string;
  slug: string;
  color: string;
  icon: string | null;
  is_system: boolean;
}

export interface Flight {
  id: string;
  display_id: string;
  project_id: string;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  ai_context: FlightAIContext;
  brand_guidelines: string | null;
  goals: FlightGoals;
  openai_thread_id: string | null;
  status: FlightStatus;
  stats: FlightStats;
  sort_order: number;
  color: string; // Hex color for calendar visualization
  attributes: FlightAttributeRef[]; // Assigned attributes (max 4)
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

interface CreateFlightInput {
  project_id: string;
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  ai_context?: FlightAIContext;
  brand_guidelines?: string;
  goals?: FlightGoals;
  color?: string;
}

interface UpdateFlightInput {
  name?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  ai_context?: FlightAIContext;
  brand_guidelines?: string;
  goals?: FlightGoals;
  color?: string;
  status?: FlightStatus;
}

interface FlightsState {
  flights: Flight[];
  currentFlight: Flight | null;
  isLoading: boolean;
  error: string | null;

  fetchFlights: (projectId: string) => Promise<void>;
  fetchFlight: (id: string) => Promise<void>;
  createFlight: (data: CreateFlightInput) => Promise<Flight>;
  updateFlight: (id: string, data: UpdateFlightInput) => Promise<void>;
  deleteFlight: (id: string) => Promise<void>;
  activateFlight: (id: string) => Promise<void>;
  pauseFlight: (id: string) => Promise<void>;
  completeFlight: (id: string) => Promise<void>;
  reactivateFlight: (id: string) => Promise<void>;
  duplicateFlight: (id: string) => Promise<Flight>;
  reorderFlights: (projectId: string, flightIds: string[]) => Promise<void>;
  triggerWorkflow: (id: string, templateId: string, postCount?: number) => Promise<void>;
  setCurrentFlight: (flight: Flight | null) => void;
  clearError: () => void;
  clearFlights: () => void;
}

export const useFlightsStore = create<FlightsState>((set) => ({
  flights: [],
  currentFlight: null,
  isLoading: false,
  error: null,

  fetchFlights: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: Flight[] }>(`/flights?project_id=${projectId}`);
      set({ flights: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Flights';
      set({ error: message, isLoading: false });
    }
  },

  fetchFlight: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: Flight }>(`/flights/${id}`);
      set({ currentFlight: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Flights';
      set({ error: message, isLoading: false });
    }
  },

  createFlight: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Flight }>('/flights', data);
      const newFlight = response.data;
      set((state) => ({
        flights: [newFlight, ...state.flights],
        isLoading: false,
      }));
      return newFlight;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen des Flights';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateFlight: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: Flight }>(`/flights/${id}`, data);
      const updatedFlight = response.data;
      set((state) => ({
        flights: state.flights.map((f) => (f.id === id ? updatedFlight : f)),
        currentFlight: state.currentFlight?.id === id ? updatedFlight : state.currentFlight,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteFlight: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`/flights/${id}`);
      set((state) => ({
        flights: state.flights.filter((f) => f.id !== id),
        currentFlight: state.currentFlight?.id === id ? null : state.currentFlight,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  activateFlight: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Flight }>(`/flights/${id}/activate`);
      const updatedFlight = response.data;
      set((state) => ({
        flights: state.flights.map((f) => (f.id === id ? updatedFlight : f)),
        currentFlight: state.currentFlight?.id === id ? updatedFlight : state.currentFlight,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktivieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  pauseFlight: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Flight }>(`/flights/${id}/pause`);
      const updatedFlight = response.data;
      set((state) => ({
        flights: state.flights.map((f) => (f.id === id ? updatedFlight : f)),
        currentFlight: state.currentFlight?.id === id ? updatedFlight : state.currentFlight,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Pausieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  completeFlight: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Flight }>(`/flights/${id}/complete`);
      const updatedFlight = response.data;
      set((state) => ({
        flights: state.flights.map((f) => (f.id === id ? updatedFlight : f)),
        currentFlight: state.currentFlight?.id === id ? updatedFlight : state.currentFlight,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Abschließen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  reactivateFlight: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Flight }>(`/flights/${id}/reactivate`);
      const updatedFlight = response.data;
      set((state) => ({
        flights: state.flights.map((f) => (f.id === id ? updatedFlight : f)),
        currentFlight: state.currentFlight?.id === id ? updatedFlight : state.currentFlight,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Reaktivieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  duplicateFlight: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Flight }>(`/flights/${id}/duplicate`);
      const newFlight = response.data;
      set((state) => ({
        flights: [...state.flights, newFlight],
        isLoading: false,
      }));
      return newFlight;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Duplizieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  reorderFlights: async (projectId, flightIds) => {
    // Optimistic update - reorder locally first
    set((state) => {
      const reorderedFlights = flightIds
        .map(id => state.flights.find(f => f.id === id))
        .filter((f): f is Flight => f !== undefined);
      return { flights: reorderedFlights };
    });

    try {
      await api.post('/flights/reorder', {
        project_id: projectId,
        flight_ids: flightIds,
      });
    } catch (error) {
      // Revert on error by refetching
      const message = error instanceof Error ? error.message : 'Fehler beim Sortieren';
      set({ error: message });
      throw error;
    }
  },

  triggerWorkflow: async (id, templateId, postCount = 5) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`/flights/${id}/trigger-workflow`, {
        template_id: templateId,
        post_count: postCount,
      });
      set({ isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Starten des Workflows';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setCurrentFlight: (flight) => {
    set({ currentFlight: flight });
  },

  clearError: () => {
    set({ error: null });
  },

  clearFlights: () => {
    set({ flights: [], currentFlight: null });
  },
}));
